import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppSurface } from '@/providers/ThemeProvider'
import { useScope } from '@/providers/ScopeProvider'
import { useAlerts } from '@/features/alerts/queries'
import { useAnomalies, useAnomalyListStream } from '@/features/anomalies/queries'
import { dockPromptsFor } from '@/features/copilot/prompts'
import { pageContextFor, routeOf } from '@/features/copilot/pageContext'
import { rcaAction } from '@/features/copilot/turnMetadata'
import { useCopilotAccess } from '@/features/copilot/access'
import { useApplyOrgFormatting } from '@/features/org/settings'
import { config } from '@/services/config'
import { AssistantDock } from './AssistantDock'
import { NavRail } from './NavRail'
import { SeverityBanner } from './SeverityBanner'
import { TopBar } from './TopBar'

/**
 * The application chassis.
 *
 * A fixed full-height shell where the document never scrolls: 48px top bar,
 * then a band holding the 212px rail, the scrolling content region, and the
 * 360px assistant dock on the inline-end side. Only the content region and
 * individual panels scroll, so navigation and scope stay put while an operator
 * works down a list.
 */

const AssistantContext = createContext(null)

/**
 * Lets any screen reach the assistant.
 *
 * ── One global chat ───────────────────────────────────────────────────────
 * The dock holds one conversation that follows the reader across every module.
 * What a question is about is not which chat it is in: it is the context chip the
 * composer prefills from the screen — `@Anomalies` on the list, `#ANM-234` on one
 * anomaly — which travels on the turn as a mention. The route travels with it.
 *
 * ── Triggers ──────────────────────────────────────────────────────────────
 * `request`, `investigate` and `quote` are TRIGGERS: a button elsewhere that drives the
 * assistant. With the dock open they continue the chat on screen; with it closed
 * they open it on a FRESH chat, so an action never lands in the middle of an old
 * conversation the reader cannot see. Opening the dock any other way continues the
 * last chat. `busy` is true while an answer is being written — the server takes one
 * question per chat at a time and there is no queue, so trigger buttons disable on
 * it.
 *
 * `request` sends one fixed sentence with one requested action — the ForgeAI
 * buttons (`ForgeAIButton`): Investigate an anomaly, Create or Update a work
 * order, a log entry, a handover. The turn carries NO mention: `mentions` is only
 * for `@`/`#` typed in the message, and the action's `module_id` is what names the
 * record. Its route is the page the button was pressed on.
 *
 * `investigate` is `request` with `request_rca`, after opening the anomaly — the
 * banner's entry point, which is pressed from somewhere else. `quote` attaches a highlighted
 * passage to the composer and sends nothing — only the reader knows the question.
 *
 * `ground` is the demo alert surfaces' entry point: it remembers the alert and
 * opens the dock like any ordinary open.
 */
export function useAssistant() {
  const context = useContext(AssistantContext)
  if (!context) throw new Error('useAssistant must be used inside AppShell')
  return context
}

/**
 * Tell the assistant what the record on this screen is called.
 *
 * A detail screen's path already carries the record's id, so the context chip and
 * the mention are known from the route alone. What the route cannot say is the
 * NAME — `#ANM-234`, `#LOG-26` — and that arrives with the screen's own fetch, so
 * the screen reports it here. Until it does, the chip reads `#…` and the mention
 * already carries the id.
 *
 * @param {string} module   the mention module (`'anomalies'`, `'logbook'`, …)
 * @param {string|undefined} id     the record's uuid
 * @param {string|undefined} label  its serial, or its name where it has no serial
 */
export function useCopilotRecord(module, id, label) {
  const { setRecord } = useAssistant()
  useEffect(() => {
    if (!id || !label) return undefined
    setRecord({ module, id, label })
    return () => setRecord((current) => (current?.id === id ? null : current))
  }, [module, id, label, setRecord])
}

/** A chat that does not exist yet; its session is created by its first question. */
const freshChat = () => ({ key: crypto.randomUUID(), sessionId: null, title: null })

export function AppShell() {
  const { t } = useTranslation('common')
  const { params } = useScope()
  /* The organisation's date order, clock and time zone, pushed into the
     formatters every screen uses. Here because it must be in force before the
     first table renders, and once rather than per page. */
  useApplyOrgFormatting()
  const [railCollapsed, setRailCollapsed] = useState(false)
  const [dockOpen, setDockOpen] = useState(false)
  const copilot = useCopilotAccess()
  /* Which anomaly, if any, is having an analysis written for it right now —
     reported up by the dock, which is the only thing that can see it. The anomaly
     screen reads it so that it shows the work happening rather than reporting, for
     the several minutes it runs, that no analysis exists. */
  const [analysing, setAnalysing] = useState(null)
  const [groundedAlert, setGroundedAlert] = useState(null)
  const [request, setRequest] = useState(null)
  const [quoted, setQuoted] = useState(null)
  /* The chat on screen. `key` remounts the conversation, so a new or reopened chat
     never renders a frame of the previous one; `sessionId` is null until the first
     question creates it. Held in memory only — a reload starts a fresh chat. */
  const [chat, setChat] = useState(freshChat)
  /* What the dock reports up: whether an answer is being written, and whether the
     chat on screen can take a question at all. */
  const [dockStatus, setDockStatus] = useState({ busy: false, blocked: false })
  const [record, setRecord] = useState(null)
  const navigate = useNavigate()
  const location = useLocation()

  useAppSurface(true)

  /**
   * The severity banner, and where its contents come from.
   *
   * Still `VITE_DEMO_MODE` only. A red strip across the top of every screen naming
   * a machine is the most load-bearing claim in the product, and whether it belongs
   * in the shipping shell at all is a product decision nobody has made — so the
   * flag that gates it is unchanged. What changed is what fills it.
   *
   * **Live.** With the backend reachable it holds the three most recent OPEN
   * anomalies **that nobody has investigated** — `rca_status=pending`, which the
   * server derives as "no analysis row, or one parked at pending". That filter is
   * what makes the band a queue rather than a list: investigating an anomaly puts
   * an analysis on it, and the next fetch no longer returns it. Real records, with
   * a real id behind each one, which is what makes `Investigate` here mean
   * something. Only the fields an anomaly actually carries are rendered; there is
   * no hours-to-trip figure and no sibling-sensor count, because the endpoint
   * reports neither.
   *
   * **Fixture.** With no backend it falls back to `useAlerts` — the prototype's
   * authored band, staged arrivals and all. Every reading and hours-to-trip figure
   * there is written by hand, and its `Investigate` has no record to open.
   *
   * Three, not one, in both cases: the prototype stages their arrival and rotates
   * between them, and that sequence is the point — a plant interrupts you, it does
   * not hand you a list. See `SeverityBanner`.
   *
   * ── The live band polls, and it polls from here ────────────────────────────
   * `useAnomalies` refetches every `ANOMALY_POLL_INTERVAL_MS` while it is mounted,
   * and this call is in the shell — so the band is re-read on every screen under
   * `/app`, not only on the anomalies list. That is the whole point of a band: the
   * thing that cannot wait for you to navigate to it has to reach you where you
   * already are.
   *
   * ── It costs nothing now, where it used to cost a request every 2s ──────
   * The band was polled for the whole session because the shell does not
   * remount between routes. It is fed by the anomaly list STREAM instead: the
   * subscription below is ref-counted and shared with the anomalies screen, so
   * the two together hold one socket, and a quiet plant costs no requests at
   * all rather than 1,800 an hour.
   */
  const bannerIsLive = config.authMode === 'live'

  const { data: liveAnomalies } = useAnomalies(
    {
      status: 'open',
      /* Anomalies nobody has investigated. This was `rcaStatus: 'pending'`,
         which the server stopped recognising — and an unknown query param is
         IGNORED by FastAPI rather than rejected, so the band has been showing
         the top three open anomalies regardless of whether they were analysed,
         silently, under a heading claiming otherwise. */
      hasAnalysis: false,
      /* Newest first. This was `sortBy: 'severity'`, and the server breaks
         severity ties on `Anomaly.id` — a random UUID — so the band showed an
         arbitrary three of the top severity, often weeks old, while a fresh
         anomaly sat unseen. The endpoint takes one sort key, so "severity,
         then newest" is not expressible; the band's job is to interrupt with
         what just happened, and the badge still carries the severity. */
      sortBy: 'created_at',
      sortDir: 'desc',
      page: 1,
      pageSize: 3,
    },
    // `enabled` on both, rather than left running: a request nobody renders is
    // a request nobody can see.
    { enabled: config.demoMode && bannerIsLive },
  )

  /* The same predicate as the query above. Subscribing without rendering the
     band would hold a socket open for a row nobody is shown. */
  useAnomalyListStream({ enabled: config.demoMode && bannerIsLive })

  const { data: bannerAlerts } = useAlerts(
    { ...params, status: 'open', sort: 'severity', pageSize: 3 },
    { enabled: config.demoMode && !bannerIsLive },
  )

  /**
   * One shape for the band, from either source.
   *
   * `anomalyId` is what separates them. An item that has one can be opened and
   * investigated for real; an item without one is a picture of an alert, and its
   * Investigate keeps the prototype's behaviour of opening the dock and no more.
   */
  const bannerItems = useMemo(() => {
    if (bannerIsLive) {
      return (liveAnomalies?.items ?? []).map((anomaly) => ({
        id: anomaly.serialNumber,
        anomalyId: anomaly.id,
        severity: anomaly.severity,
        // The detector writes the title as "{sensor}: {what happened}", which is
        // already the sentence this band wants. Composing one from parts here would
        // be a second, worse phrasing of the same fact.
        headline: anomaly.title,
        // Plain strings on the wire, not nodes — `hierarchy: {plant, line, machine}`
        // carries names and no ids, which is also why the band cannot link to a line.
        assetName: anomaly.hierarchy.machine,
        lineName: anomaly.hierarchy.line,
        detectedAt: anomaly.createdAt,
      }))
    }
    return bannerAlerts?.items ?? []
  }, [bannerIsLive, liveAnomalies, bannerAlerts])

  const ground = useCallback((alert) => {
    setGroundedAlert(alert)
    setDockOpen(true)
  }, [])

  const newChat = useCallback(() => setChat(freshChat()), [])
  const openChat = useCallback(
    (session) => setChat({ key: crypto.randomUUID(), sessionId: session.id, title: session.title ?? null }),
    [],
  )
  /* The first question created the session, or a stuck one was replaced. Same
     key: the conversation on screen is the one that did it, and must not remount. */
  const onSession = useCallback(
    (session) =>
      setChat((current) => ({ ...current, sessionId: session?.id ?? null, title: session?.title ?? null })),
    [],
  )

  /**
   * Open the dock for a trigger.
   *
   * Closed → a fresh chat. Open → the chat on screen, unless that chat cannot take
   * a question (its last one was never answered), in which case a fresh chat is the
   * only place the trigger can land.
   *
   * Returns the key of the chat the trigger is FOR. The trigger carries it, and only
   * that chat consumes it — otherwise the next new chat would find the same pending
   * Investigate in the shell and run it again.
   */
  const openForTrigger = useCallback(() => {
    let key = chat.key
    if (!dockOpen || dockStatus.blocked) {
      const next = freshChat()
      setChat(next)
      key = next.key
    }
    setDockOpen(true)
    return key
  }, [chat.key, dockOpen, dockStatus.blocked])

  /**
   * Send one requested action, from a ForgeAI button.
   *
   * A token rather than a flag, so pressing the same button twice is a second
   * request rather than a no-op. The dock consumes it once.
   *
   * @param {string} message the sentence the turn carries — the button's own
   * @param {{type: string, module: string, module_id: string|null}} action see `requestedAction`
   * @param {string} [route] defaults to the page underneath
   * @param {string} [chip] the context written in front of the message — `#WO-12`;
   *   defaults to the chip the dock shows for the page underneath
   */
  const ask = useCallback(
    ({ message, action, route, chip }) => {
      if (!message || !action || dockStatus.busy) return
      const chatKey = openForTrigger()
      setRequest({
        chatKey,
        token: crypto.randomUUID(),
        message,
        action,
        chip,
        route: route ?? routeOf(location.pathname),
        /* The radar on the anomaly screen follows a requested analysis. */
        analysing: action.type === 'request_rca' ? action.module_id : null,
      })
    },
    [location.pathname, openForTrigger, dockStatus.busy],
  )

  /**
   * Open an anomaly and have the copilot investigate it.
   *
   * The navigation is conditional: `navigate` to the path you are already on
   * pushes a second history entry for the same screen. The route is the anomaly's
   * own, since this runs a frame before the location updates.
   */
  const investigate = useCallback(
    (anomaly) => {
      if (!anomaly?.id || dockStatus.busy) return
      const target = `/app/anomalies/${anomaly.id}`
      ask({
        message: t('app:dock.forgeActions.investigateAnomaly.message'),
        action: rcaAction(anomaly.id),
        route: routeOf(target),
        /* Explicit: the page underneath is not the anomaly yet. */
        chip: anomaly.serialNumber ? `#${anomaly.serialNumber}` : null,
      })
      if (location.pathname !== target) navigate(target)
    },
    [ask, navigate, location.pathname, dockStatus.busy, t],
  )

  /**
   * Open the dock with a passage attached to its composer.
   *
   * Stages a citation and stops; the turn is sent when the reader sends it. A token
   * so that two identical selections are two quotes, and so a re-render does not
   * deliver the same one twice.
   */
  const quote = useCallback(
    (text) => {
      if (!text || dockStatus.busy) return
      const chatKey = openForTrigger()
      setQuoted({ chatKey, token: crypto.randomUUID(), text })
    },
    [openForTrigger, dockStatus.busy],
  )

  /** The band carries its own item shape; the copilot needs the anomaly behind it. */
  const investigateBannerItem = useCallback(
    (item) => {
      if (!item?.anomalyId) return
      investigate({ id: item.anomalyId, serialNumber: item.id })
    },
    [investigate],
  )

  /**
   * What the screen underneath says about the next question: its route, its
   * module, the record it shows and — once the screen has reported it — that
   * record's name. Resolved here because the shell is what knows both the route
   * and the dock; see `features/copilot/pageContext`.
   */
  const context = useMemo(() => {
    const page = pageContextFor(location.pathname)
    const label =
      page.recordId && record?.module === page.module && record?.id === page.recordId ? record.label : null
    return { ...page, label, prompts: dockPromptsFor(page) }
  }, [location.pathname, record])

  const assistant = useMemo(
    () => ({
      alert: groundedAlert,
      ground,
      request: ask,
      investigate,
      quote,
      analysing,
      busy: dockStatus.busy,
      setRecord,
      open: () => setDockOpen(true),
      close: () => setDockOpen(false),
      isOpen: dockOpen,
    }),
    [groundedAlert, ground, ask, investigate, quote, analysing, dockStatus.busy, dockOpen],
  )

  return (
    <AssistantContext.Provider value={assistant}>
      <a
        href="#content"
        className="sr-only-focusable absolute z-50 m-2 rounded-sm bg-card px-3 py-2 text-control shadow-lift"
      >
        {t('nav.skipToContent')}
      </a>

      <div className="flex h-screen flex-col">
        {/* The assistant is offered to anyone signed in — the backend has no
            copilot permission any more. See `features/copilot/access`. */}
        <TopBar
          railCollapsed={railCollapsed}
          onToggleRail={() => setRailCollapsed((current) => !current)}
          onOpenAssistant={copilot.granted ? () => setDockOpen(true) : null}
        />

        <div className="flex min-h-0 flex-1">
          <NavRail collapsed={railCollapsed} />

          <main className="flex min-w-0 flex-1 flex-col">
            {config.demoMode ? (
              <SeverityBanner
                alerts={bannerItems}
                staged={!bannerIsLive}
                onInvestigate={bannerIsLive ? investigateBannerItem : ground}
                investigateDisabled={bannerIsLive && dockStatus.busy}
              />
            ) : null}
            <div id="content" className="min-h-0 flex-1 overflow-y-auto [&>*]:min-h-full">
              <Outlet />
            </div>
          </main>

          <AssistantDock
            open={dockOpen}
            chat={chat}
            context={context}
            request={request}
            quoted={quoted}
            busy={dockStatus.busy}
            onNewChat={newChat}
            onOpenChat={openChat}
            onSession={onSession}
            onStatus={setDockStatus}
            onAnalysing={setAnalysing}
            onClose={() => setDockOpen(false)}
          />
        </div>
      </div>
    </AssistantContext.Provider>
  )
}

/**
 * Page frame inside the content region. The 1700px cap matters: several screens
 * are 12-column tables that would otherwise stretch to unreadable line lengths
 * on an ultrawide control-room display.
 */
export function Page({ className, children }) {
  return <div className={`max-w-[1700px] px-8 pt-5.5 pb-10 max-xl:px-5.5 ${className ?? ''}`}>{children}</div>
}
